import type { AnchorHTMLAttributes } from 'react';

import { cn } from '#/utils/cn';

import { Icon } from './icons';
import { resolveLinkTarget } from './linkTarget';

interface ArrowLinkProps extends Pick<
  AnchorHTMLAttributes<HTMLAnchorElement>,
  'target' | 'rel'
> {
  href: string;
  children: string;
  className?: string;
  /** GTM `data-track` id, forwarded onto the anchor. */
  dataTrack?: string;
  /** GTM `data-track-label`, forwarded onto the anchor. */
  dataTrackLabel?: string;
  /** GTM `data-track-context`, forwarded onto the anchor. */
  dataTrackContext?: string;
}

/**
 * A standalone text link trailed by an arrow that nudges forward on hover.
 */
export function ArrowLink({
  href,
  children,
  className,
  target,
  rel,
  dataTrack,
  dataTrackLabel,
  dataTrackContext,
}: ArrowLinkProps) {
  const linkProps = resolveLinkTarget({ href, target, rel });

  return (
    <a
      href={href}
      target={linkProps.target}
      rel={linkProps.rel}
      data-track={dataTrack}
      data-track-label={dataTrackLabel}
      data-track-context={dataTrackContext}
      className={cn(
        'group text-static-14 inline-flex items-center gap-12 leading-none font-semibold uppercase no-underline',
        className,
      )}
    >
      <span>{children}</span>
      <Icon
        id="arrow-right"
        size={18}
        aria-hidden="true"
        className="shrink-0 transition-transform group-hover:translate-x-4"
      />
    </a>
  );
}
